/**
 * Asset Positioning Constants
 * Defines realistic heights, zones, and placement rules for VR asset positioning
 */

// Realistic heights for different asset categories (in meters)
export const HEIGHT_RULES = {
    // Wall-mounted equipment (eye level or overhead)
    safety_button: { y: 1.4, reason: "Eye level for visibility and quick access" },
    fire_extinguisher: { y: 1.2, reason: "Wall-mounted at accessible height" },
    control_panel: { y: 1.3, reason: "Chest height for comfortable operation" },
    wall_switch: { y: 1.2, reason: "Standard wall switch height" },
    wall_poster: { y: 1.6, reason: "Eye level on wall for readability" },
    emergency_alarm: { y: 1.5, reason: "Eye level for visibility" },
    wall_clock: { y: 2.0, reason: "Above eye level for visibility" },
    wall_cabinet: { y: 1.8, reason: "Upper wall cabinet height" },

    // Workbench/Table items (waist to chest height)
    tool: { y: 0.75, reason: "Standard workbench/table height" },
    manual: { y: 0.76, reason: "Slightly raised on table" },
    small_part: { y: 0.75, reason: "Table height for assembly work" },
    instrument: { y: 0.75, reason: "Table height for precision work" },
    container: { y: 0.75, reason: "Table or shelf height" },
    testing_equipment: { y: 0.75, reason: "Workbench height for testing" },
    computer_monitor: { y: 0.9, reason: "Table with monitor stand" },
    keyboard: { y: 0.75, reason: "Table height for typing" },

    // Floor-level items
    floor_marking: { y: 0.01, reason: "Floor level (slightly raised to prevent z-fighting)" },
    floor_mat: { y: 0.005, reason: "Flush with floor" },
    large_machine: { y: 0, reason: "Floor-standing heavy equipment" },
    pallet: { y: 0.15, reason: "Standard pallet height" },
    cart: { y: 0, reason: "Floor-level on wheels" },
    forklift: { y: 0, reason: "Floor-standing vehicle" },

    // Shelf/Cabinet items (waist to shoulder height)
    safety_equipment: { y: 0.5, reason: "Lower shelf/cabinet height" },
    ppe_helmet: { y: 1.2, reason: "Shelf or hook at shoulder height" },
    ppe_gloves: { y: 1.0, reason: "Mid-shelf height" },
    ppe_goggles: { y: 1.2, reason: "Shelf at shoulder height" },
    supplies: { y: 1.0, reason: "Mid-shelf storage" },

    // Overhead items
    ceiling_light: { y: 2.5, reason: "Ceiling-mounted lighting" },
    overhead_crane: { y: 3.0, reason: "Industrial overhead crane" },
    ventilation: { y: 2.8, reason: "Ceiling ventilation system" },
    sprinkler: { y: 2.7, reason: "Fire suppression system" },

    // Handheld items (when placed down)
    handheld_tool: { y: 0.75, reason: "Placed on workbench when not in use" },
    phone: { y: 0.75, reason: "Table height" },
    tablet: { y: 0.75, reason: "Table height" },
    clipboard: { y: 0.75, reason: "Table or hanging at waist height" },

    // Default fallbacks by size
    small_object: { y: 0.75, reason: "Table height for small items" },
    medium_object: { y: 0.5, reason: "Lower shelf for medium items" },
    large_object: { y: 0, reason: "Floor level for large items" },
    default: { y: 1.0, reason: "Default chest height for unknown items" }
};

// Asset category keywords for matching
export const CATEGORY_KEYWORDS = {
    safety_button: ['button', 'emergency button', 'stop button', 'panic button', 'e-stop'],
    fire_extinguisher: ['fire extinguisher', 'extinguisher', 'fire safety'],
    control_panel: ['control panel', 'panel', 'control board', 'switchboard', 'console'],
    wall_switch: ['switch', 'light switch', 'power switch', 'wall switch'],
    wall_poster: ['poster', 'sign', 'notice', 'placard', 'information board'],

    tool: ['screwdriver', 'wrench', 'hammer', 'pliers', 'drill', 'saw'],
    manual: ['manual', 'handbook', 'guide', 'documentation', 'book'],
    small_part: ['screw', 'bolt', 'nut', 'washer', 'pin', 'component', 'part'],

    floor_marking: ['floor marking', 'floor tape', 'floor line', 'floor arrow'],
    large_machine: ['machine', 'equipment', 'press', 'lathe', 'milling', 'cnc'],

    safety_equipment: ['safety helmet', 'hard hat', 'safety vest', 'earplugs', 'mask'],
    ppe_helmet: ['helmet', 'hard hat', 'safety helmet'],
    ppe_gloves: ['gloves', 'safety gloves', 'protective gloves'],
    ppe_goggles: ['goggles', 'safety glasses', 'eye protection'],

    ceiling_light: ['light', 'ceiling light', 'overhead light', 'lamp'],

    handheld_tool: ['handheld', 'portable', 'cordless']
};

// Spatial zones for organizing assets
export const SPATIAL_ZONES = {
    workbench: {
        center: { x: 0, y: 0.75, z: 0 },
        dimensions: { width: 2, depth: 1, height: 0.1 },
        capacity: 12, // Max items
        description: "Main work area for tools and assembly"
    },

    floor_area: {
        center: { x: 0, y: 0, z: 0 },
        radius: 5,
        description: "Floor space for large equipment and machines"
    },

    wall_left: {
        center: { x: -3, y: 1.5, z: -2 },
        spacing: 0.5,
        vertical_spacing: 0.4,
        capacity: 8,
        description: "Left wall for mounted equipment and panels"
    },

    wall_right: {
        center: { x: 3, y: 1.5, z: -2 },
        spacing: 0.5,
        vertical_spacing: 0.4,
        capacity: 8,
        description: "Right wall for mounted equipment and panels"
    },

    wall_back: {
        center: { x: 0, y: 1.5, z: -3 },
        spacing: 0.5,
        vertical_spacing: 0.4,
        capacity: 10,
        description: "Back wall for posters, controls, and storage"
    },

    tool_rack: {
        center: { x: 2, y: 1.2, z: -1.5 },
        spacing: 0.3,
        vertical_spacing: 0.3,
        capacity: 15,
        description: "Tool storage rack"
    },

    safety_station: {
        center: { x: -2.5, y: 0.5, z: 1.5 },
        radius: 0.8,
        capacity: 6,
        description: "PPE and safety equipment station near entrance"
    },

    inspection_area: {
        center: { x: 1.5, y: 0.75, z: 1.5 },
        radius: 1,
        capacity: 5,
        description: "Quality inspection and testing area"
    },

    storage_shelf: {
        center: { x: -2, y: 1.0, z: -2 },
        spacing: 0.4,
        vertical_spacing: 0.5,
        capacity: 12,
        description: "Storage shelf for supplies and materials"
    }
};

// Training layout patterns
export const LAYOUT_PATTERNS = {
    manufacturing: {
        name: "Manufacturing/Assembly",
        keywords: ['assembly', 'manufacturing', 'production', 'fabrication', 'build'],
        zones: ['workbench', 'tool_rack', 'inspection_area', 'storage_shelf'],
        workflow_direction: 'left_to_right', // Assets arranged in sequence
        description: "Linear workflow for assembly and production processes"
    },

    safety_training: {
        name: "Safety Training",
        keywords: ['safety', 'ppe', 'hazard', 'emergency', 'protection'],
        zones: ['safety_station', 'wall_back', 'floor_area'],
        workflow_direction: 'radial', // Safety equipment around perimeter
        description: "Safety equipment arranged for easy identification and access"
    },

    inspection_procedure: {
        name: "Inspection/Quality Control",
        keywords: ['inspection', 'quality', 'check', 'test', 'verify', 'measure'],
        zones: ['inspection_area', 'workbench', 'tool_rack'],
        workflow_direction: 'centered', // Tools around inspection point
        description: "Tools and equipment arranged around central inspection area"
    },

    maintenance: {
        name: "Maintenance/Repair",
        keywords: ['maintenance', 'repair', 'service', 'fix', 'troubleshoot'],
        zones: ['workbench', 'tool_rack', 'floor_area', 'storage_shelf'],
        workflow_direction: 'flexible',
        description: "Flexible layout for maintenance procedures"
    },

    operation: {
        name: "Equipment Operation",
        keywords: ['operate', 'control', 'run', 'start', 'shutdown', 'monitor'],
        zones: ['wall_back', 'floor_area', 'workbench'],
        workflow_direction: 'operator_facing', // Controls facing operator position
        description: "Control panels and equipment arranged for operator access"
    }
};

// Collision detection parameters
export const COLLISION_PARAMS = {
    min_spacing: 0.3, // Minimum 30cm between objects
    default_object_radius: 0.25, // Default bounding sphere radius
    floor_clearance: 0.05, // Minimum clearance from floor
    wall_clearance: 0.1 // Minimum clearance from walls
};

// VR ergonomics (standard heights for human interaction)
export const VR_ERGONOMICS = {
    FLOOR: 0,
    ANKLE: 0.2,
    KNEE: 0.5,
    WAIST: 1.0,
    CHEST: 1.3,
    SHOULDER: 1.5,
    EYE_LEVEL: 1.65,
    OVERHEAD: 2.0,
    CEILING: 2.5,

    // Reach distances
    COMFORTABLE_REACH: 0.6, // 60cm comfortable forward reach
    MAX_REACH: 1.0, // 1m maximum reach

    // Interaction zones
    PRIMARY_INTERACTION_ZONE: {
        height_min: 0.7,
        height_max: 1.6,
        distance_max: 1.0
    }
};

/**
 * Get appropriate height for an asset based on its name and description
 */
export function getAssetHeight(assetName, description = '') {
    const lowerName = assetName.toLowerCase();
    const lowerDesc = description.toLowerCase();
    const searchText = `${lowerName} ${lowerDesc}`;

    // Check specific categories first
    for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
        for (const keyword of keywords) {
            if (searchText.includes(keyword)) {
                return HEIGHT_RULES[category] || HEIGHT_RULES.default;
            }
        }
    }

    // Check for size hints
    if (searchText.includes('small') || searchText.includes('tiny')) {
        return HEIGHT_RULES.small_object;
    }
    if (searchText.includes('large') || searchText.includes('big') || searchText.includes('heavy')) {
        return HEIGHT_RULES.large_object;
    }

    // Check for placement hints
    if (searchText.includes('wall') || searchText.includes('mounted')) {
        return HEIGHT_RULES.control_panel;
    }
    if (searchText.includes('floor') || searchText.includes('ground')) {
        return HEIGHT_RULES.large_object;
    }
    if (searchText.includes('table') || searchText.includes('desk') || searchText.includes('bench')) {
        return HEIGHT_RULES.tool;
    }
    if (searchText.includes('shelf') || searchText.includes('cabinet')) {
        return HEIGHT_RULES.safety_equipment;
    }
    if (searchText.includes('ceiling') || searchText.includes('overhead')) {
        return HEIGHT_RULES.ceiling_light;
    }

    // Default
    return HEIGHT_RULES.default;
}

/**
 * Categorize asset for placement strategy
 */
export function categorizeAsset(assetName, description = '') {
    const searchText = `${assetName.toLowerCase()} ${description.toLowerCase()}`;

    // Check category keywords
    for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
        for (const keyword of keywords) {
            if (searchText.includes(keyword)) {
                // Map category to placement zone
                if (category.startsWith('wall_') || category.includes('panel') || category.includes('switch')) {
                    return { zone: 'wall_mounted', category };
                }
                if (category.includes('floor') || category.includes('large_machine')) {
                    return { zone: 'floor_equipment', category };
                }
                if (category.includes('tool') && searchText.includes('handheld')) {
                    return { zone: 'handheld_tool', category };
                }
                if (category.includes('safety') || category.includes('ppe')) {
                    return { zone: 'safety_equipment', category };
                }
                if (category.includes('tool') || category.includes('small_part')) {
                    return { zone: 'workbench_item', category };
                }
            }
        }
    }

    // Default categorization based on hints
    if (searchText.includes('safety') || searchText.includes('ppe')) {
        return { zone: 'safety_equipment', category: 'safety_equipment' };
    }
    if (searchText.includes('wall')) {
        return { zone: 'wall_mounted', category: 'wall_switch' };
    }
    if (searchText.includes('floor') || searchText.includes('large') || searchText.includes('machine')) {
        return { zone: 'floor_equipment', category: 'large_machine' };
    }

    // Default to workbench
    return { zone: 'workbench_item', category: 'tool' };
}

/**
 * Detect training layout pattern from content
 */
export function detectLayoutPattern(content) {
    const lowerContent = content.toLowerCase();
    let maxScore = 0;
    let detectedPattern = 'manufacturing'; // default

    for (const [patternKey, pattern] of Object.entries(LAYOUT_PATTERNS)) {
        let score = 0;
        for (const keyword of pattern.keywords) {
            // Count occurrences of keywords
            const regex = new RegExp(keyword, 'gi');
            const matches = lowerContent.match(regex);
            score += matches ? matches.length : 0;
        }

        if (score > maxScore) {
            maxScore = score;
            detectedPattern = patternKey;
        }
    }

    return {
        pattern: detectedPattern,
        config: LAYOUT_PATTERNS[detectedPattern],
        confidence: Math.min(maxScore / 10, 1.0) // Normalize to 0-1
    };
}
